import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import Modal from "components/modal";
import ProductionModalPicker from "components/Picker/ProductionPicker/ProductionModalPicker";
import TagModalPicker from "components/Picker/TagPicker/TagModalPicker";
import React from "react";
import { Button } from "semantic-ui-react";
import { nounToLower } from "utilities/translationUtils";

interface Props<T> {
  afterDeleteRedirectUrl: string;
  associatedItemCount: number;
  isOpen: boolean
  onClose: () => void
  moveSingularLabel: string;
  movePluralLabel: string;
  renderSelect: (value: T | null, onChange: (value: T | null) => void, error: boolean) => React.ReactNode;
  deleteFunc: (moveTo: "dontmove" | "moveto" | "noselection", selectedItem: T | null) => Promise<any>;
}

const DeleteMoveItems = <T extends any>(props: Props<T>) => {
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const [moveTo, setMoveTo] = React.useState<"dontmove" | "moveto" | "noselection">("noselection");
  const [radioError, setRadioError] = React.useState<boolean>(false);
  const [selectItemError, setSelectItemError] = React.useState<boolean>(false);
  const [selectedItem, setSelectedItem] = React.useState<T | null>(null);

  const displayMoveTo = props.associatedItemCount > 0;

  const countLabel = (props.associatedItemCount === 1)
    ? `1 ${props.moveSingularLabel}`
    : `${props.associatedItemCount} ${props.movePluralLabel}`;

  const onDelete = async () => {
    if (displayMoveTo && moveTo === "noselection") {
      setRadioError(true);
      return;
    }

    if (displayMoveTo && moveTo === "moveto" && !selectedItem) {
      setSelectItemError(true);
      return;
    }

    setDeleting(true);
    await props.deleteFunc(moveTo, selectedItem);
    window.location.href = props.afterDeleteRedirectUrl;
    setDeleting(false);
  }

  return <Modal
    isOpen={props.isOpen}
    width="mini"
    handleClose={props.onClose}
    title={RESX.Buttons.btnDelete}
    className="force_z"
  >
    <div className="flex-column gap-large">
      {!displayMoveTo && <div>
        {RESX.GeneralWarnings.AreYouSure}
      </div>}
      {displayMoveTo && <div className="flex-column gap">
        <div className="margin-bottom">
          {RESX.GeneralLabels.WhatDoYouWantToDoWithAssociatedItems}
        </div>
        <RadioGroup
          error={radioError}
          orientation="vertical"
          options={[{
            label: RESX.GeneralLabels.UnlinkX.replace("{0}", countLabel),
            value: "dontmove"
          }, {
            label: RESX.GeneralLabels.MoveX.replace("{0}", countLabel),
            value: "moveto"
          }]}
          value={moveTo}
          onChange={value => { setMoveTo(value); setRadioError(false); }}
        />
        <div style={{ marginLeft: 24, visibility: moveTo === "moveto" ? "visible" : "hidden" }}>
          {props.renderSelect(selectedItem, (v) => { setSelectedItem(v); setSelectItemError(false) }, selectItemError)}
        </div>
      </div>}
      <div className="flex">
        <div className="grow1"></div>
        <div>
          <Button
            color="red"
            onClick={onDelete}
            content={RESX.Buttons.btnDelete}
            name="Delete"
            loading={deleting}
          />
        </div>
      </div>
    </div>
  </Modal>
}

interface DeleteAndMoveProductionsProps {
  productionId: number;
  afterDeleteRedirectUrl: string;
  associatedItemCount: number;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteAndMoveProductions = (props: DeleteAndMoveProductionsProps) => {
  return <DeleteMoveItems<server.dto.TourSmall>
    afterDeleteRedirectUrl={props.afterDeleteRedirectUrl}
    associatedItemCount={props.associatedItemCount}
    isOpen={props.isOpen}
    onClose={props.onClose}
    moveSingularLabel={nounToLower(site.currentEnvironment.show.singular)}
    movePluralLabel={nounToLower(site.currentEnvironment.show.plural)}
    renderSelect={(value, onChange, error) => (
      <ProductionModalPicker
        canCreateNew={false}
        excludeKeys={[props.productionId]}
        nonInPageEditing={{
          onChange,
          legacyHasError: error,
          value,
          validatorKey: "production",
          required: true,
        }}
      />
    )}
    deleteFunc={(moveTo, selectedItem) => window.CoreApi.Production.Delete({
      moveToProductionId: moveTo === "moveto" ? selectedItem.productionId : null,
      productionId: props.productionId,
    })}
  />
}

interface DeleteAndMoveTagsProps {
  mainArea: server.dto.MainAreas;
  tagId: number;
  afterDeleteRedirectUrl: string;
  associatedItemCount: number;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteAndMoveTags = (props: DeleteAndMoveTagsProps) => {
  return <DeleteMoveItems<server.dto.TagForList>
    afterDeleteRedirectUrl={props.afterDeleteRedirectUrl}
    associatedItemCount={props.associatedItemCount}
    isOpen={props.isOpen}
    onClose={props.onClose}
    moveSingularLabel={nounToLower(site.currentEnvironment[props.mainArea].singular)}
    movePluralLabel={nounToLower(site.currentEnvironment[props.mainArea].plural)}
    renderSelect={(value, onChange, error) => (
      <TagModalPicker
        mainArea={props.mainArea}
        canCreateNew={false}
        excludeKeys={[props.tagId]}
        nonInPageEditing={{
          onChange,
          legacyHasError: error,
          value,
          validatorKey: "tag",
          required: true,
        }}
      />
    )}
    deleteFunc={(moveTo, selectedItem) => {
      if (moveTo === "dontmove" || moveTo === "noselection") {
        return window.CoreApi.Tag.Delete(props.tagId);
      }

      return window.CoreApi.Tag.DeleteAndMove(props.tagId, selectedItem.tagId);
    }}
  />
}